export const fontShrink = (size = 0) => size * 0.28;

// --- ImageSelect helpers ---
//
// Pure data-shape helpers for the multi-select photo_filters working array.
// Colocated here (rather than as ImageSelect statics) so a consumer that only
// needs the data shape — e.g. EditChannel's reseed logic — can depend on this
// util module instead of importing the ImageSelect UI component. ImageSelect
// itself re-exports these as static properties for backwards compatibility.

// Resolve the display name for any filter item shape:
//   {file: File}          → File.name
//   {file: {name: "url"}} → "url"
//   {image: "url"}        → "url"
//   "string"              → "string"
export const resolveDisplayName = (item) => {
  if (!item) return null;
  if (item.file instanceof File) return item.file.name;
  if (item.file && item.file.name) return item.file.name;
  if (item.image) return item.image;
  if (item.name) return item.name;
  if (typeof item === 'string') return item;
  return null;
};

// Reduce a display name to its bare filename. A URL basename comes from the
// path (dropping any query/signature); a value that isn't a URL is already a
// filename, so we defensively strip a path prefix + query and return it.
export const basename = (name) => {
  if (!name) return name;
  try {
    return new URL(name).pathname.split("/").pop() || name;
  } catch {
    return name.split("/").pop().split("?")[0] || name;
  }
};

// Merge newly-selected upload(s) into the working array WITHOUT downgrading
// already-staged filters. Each existing entry is kept exactly as-is — a real
// File stays an uploadable File; a { name: url } reference stays a reference —
// and every file in the incoming FileList is appended unless an entry with the
// same display name is already staged (dedupe).
//
// This replaces the earlier logic that remapped every existing File to a
// bare-name reference and kept only the FIRST new file. That downgrade meant a
// channel could never send more than one fresh File per save, so uploading
// several frames (or adding them one at a time) silently persisted only one —
// the multi-filter data-loss bug guests hit on the iPad.
export const mergeUploads = (prevValue, fileList) => {
  const result = Array.isArray(prevValue) ? prevValue.filter(Boolean) : [];
  // Dedupe by BASENAME: an existing entry's display name is a full S3 URL for
  // a server-loaded reference ("https://cdn/filters/a.png") but a bare
  // filename ("a.png") for a staged File, while an incoming upload always
  // carries the bare filename. Normalising both to the basename keeps a
  // re-upload of an already-present frame from producing a duplicate entry.
  const seen = new Set(
    result
      .map((x) => basename(resolveDisplayName(x)))
      .filter(Boolean),
  );
  Array.from(fileList || []).forEach((file) => {
    if (!file) return;
    const name = basename(file.name);
    if (name && seen.has(name)) return;
    result.push({ file, selected: false });
    if (name) seen.add(name);
  });
  return result;
};

// Load-time normalization for the multi-select photo_filters value.
//
// The v2 channel API serves each filter with a `position` field but does NOT
// guarantee the array itself is ordered by it. The dashboard (DraggableList)
// and the iPad must agree on filter order, and the iPad sorts by ascending
// `position` — so we sort here on load to match. This is display/load order
// ONLY: once the user reorders via DraggableList the working-array order is
// the user's intent and is persisted separately (the position field is not
// carried onto the working { file, selected } shape).
//
// Stable sort by ascending `position`; entries with no/undefined position
// sort last while preserving their relative input order.
export const sortByPosition = (value) => {
  return value
    .map((item, index) => ({ item, index }))
    .sort((a, b) => {
      // Number.isFinite (not `typeof === "number"`): typeof NaN is "number",
      // so a malformed `position: NaN` would otherwise be treated as a real
      // value and `NaN - pb` would defeat the comparator. Treat NaN/Infinity
      // like a missing position → sorts last.
      const pa =
        a.item && Number.isFinite(a.item.position)
          ? a.item.position
          : Number.POSITIVE_INFINITY;
      const pb =
        b.item && Number.isFinite(b.item.position)
          ? b.item.position
          : Number.POSITIVE_INFINITY;
      // Stable tie-break on original index (covers equal positions and the
      // both-missing case where pa === pb === Infinity).
      return pa - pb || a.index - b.index;
    })
    .map(({ item }) => item);
};

// Carry the server-side `id` onto a working entry, when the source has one.
//
// This is what makes deletion an EXPLICIT instruction rather than something
// Rails infers from a filter's absence in the payload. Absence cannot tell a
// deliberate removal apart from a dropped reference, a partial save, or a
// stale payload — which is how a customer's branded filter silently vanished
// mid-event. Carrying the id lets the client name what the user removed.
//
// A freshly-uploaded File has no server row, so it must carry NO id at all —
// the key is omitted rather than set to null, because a null would serialise
// to "null" on the wire and match no row.
export const withId = (entry, source) => {
  return source && source.id !== undefined && source.id !== null
    ? { ...entry, id: source.id }
    : entry;
};
